import { queryOptions, useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { Pencil, Plus, Tags, Trash2 } from "lucide-react"
import { useState } from "react"

import { ChangelogTabs } from "@/components/changelog/changelog-tabs"
import { DeleteTagDialog } from "@/components/changelog/delete-tag-dialog"
import { TagFormDialog } from "@/components/changelog/tag-form-dialog"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { listTags } from "@/http/changelog/tags"
import type { TChangelogTag } from "@/http/changelog/types"

const tagsQueryOptions = (serviceId: string, organizationSlug: string) =>
  queryOptions({
    queryKey: ["changelog-tags", serviceId, organizationSlug],
    queryFn: async () => {
      const response = await listTags({
        serviceId,
        organizationSlug,
      })
      if (response.error) throw new Error(response.error.message)
      return response.data
    },
  })

export const Route = createFileRoute(
  "/_authenticated/$orgSlug/changelog/services/$serviceId/tags",
)({
  loader: async ({ context, params }) => {
    await context.queryClient.ensureQueryData(
      tagsQueryOptions(params.serviceId, params.orgSlug),
    )
    return {
      title: "Tags",
      path: `/${params.orgSlug}/changelog/services/${params.serviceId}/tags`,
    }
  },
  component: TagsPage,
})

function TagsPage() {
  const { orgSlug, serviceId } = Route.useParams()
  const { data: tags, refetch } = useSuspenseQuery(
    tagsQueryOptions(serviceId, orgSlug),
  )

  const [formDialog, setFormDialog] = useState<{
    open: boolean
    tag?: TChangelogTag
  }>({ open: false })

  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean
    tag: TChangelogTag | null
  }>({ open: false, tag: null })

  const handleSuccess = () => {
    refetch()
  }

  return (
    <div className="space-y-6">
      <ChangelogTabs orgSlug={orgSlug} serviceId={serviceId} activeTab="tags" />
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <Tags className="text-muted-foreground size-6" />
          <div>
            <h1 className="text-2xl font-bold md:text-3xl">Tags</h1>
            <p className="text-muted-foreground">
              Gerencie as tags do changelog
            </p>
          </div>
        </div>
        <Button onClick={() => setFormDialog({ open: true, tag: undefined })}>
          <Plus className="mr-2 size-4" />
          Nova tag
        </Button>
      </div>

      {tags.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground mb-4">Nenhuma tag cadastrada</p>
            <Button
              onClick={() => setFormDialog({ open: true, tag: undefined })}
            >
              <Plus className="mr-2 size-4" />
              Crie sua primeira tag
            </Button>
          </CardContent>
        </Card>
      )}

      {tags.length > 0 && (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Cor</TableHead>
                  <TableHead>Nome</TableHead>
                  <TableHead>Slug</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tags.map((tag) => (
                  <TableRow key={tag.id}>
                    <TableCell>
                      <div
                        className="size-4 rounded-full"
                        style={{
                          backgroundColor: tag.color,
                        }}
                      />
                    </TableCell>
                    <TableCell className="font-medium">{tag.name}</TableCell>
                    <TableCell>
                      <code className="text-muted-foreground text-xs">
                        {tag.slug}
                      </code>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() =>
                            setFormDialog({
                              open: true,
                              tag,
                            })
                          }
                        >
                          <Pencil className="size-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() =>
                            setDeleteDialog({
                              open: true,
                              tag,
                            })
                          }
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <TagFormDialog
        open={formDialog.open}
        onOpenChange={(open) =>
          setFormDialog({ open, tag: open ? formDialog.tag : undefined })
        }
        serviceId={serviceId}
        organizationSlug={orgSlug}
        tag={formDialog.tag}
        onSuccess={handleSuccess}
      />

      {deleteDialog.tag && (
        <DeleteTagDialog
          open={deleteDialog.open}
          onOpenChange={(open) =>
            setDeleteDialog({
              open,
              tag: open ? deleteDialog.tag : null,
            })
          }
          tagId={deleteDialog.tag.id}
          tagName={deleteDialog.tag.name}
          serviceId={serviceId}
          organizationSlug={orgSlug}
          onSuccess={handleSuccess}
        />
      )}
    </div>
  )
}
